import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface IssuesWeSolveProps {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
}

export default function IssuesWeSolve({ 
  primaryColor = '#4a6fa1',
  secondaryColor = '#f8f9fa', 
  accentColor = '#e63946'
}: IssuesWeSolveProps) {
  const navigate = useNavigate();
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  
  const toggleCard = (index: number) => {
    setExpandedCard(expandedCard === index ? null : index);
  };
  
  const issues = [
    {
      title: "Panic Attacks",
      content: "A panic attack is the abrupt onset of intense fear or discomfort that reaches a peak within minutes and includes at least four of the following symptoms: Palpitations, pounding heart, or accelerated heart rate. Sweating. Trembling or shaking. Sensations of shortness of breath or smothering."
    },
    {
      title: "Addictions, OCD Or Lying",
      content: "Obsessive thoughts and compulsive behaviors (OCD), gambling, alcohol, drugs, pornography, internet or sex, compulsive lying, etc."
    },
    {
      title: "Avoidance Or Phobia/Fear Of",
      content: "Public speaking, driving. traveling, sickness, strangers, places, dating, sex, physical touch, difficult conversations, difficult people, death/dying, etc."
    },
    {
      title: "Depression Or Low Mood",
      content: "Feeling hopeless, lonely, sad, over or under sleeping, tiredness, low motivation or energy."
    },
    {
      title: "Trauma, Abuse And PTSD",
      content: "Unpleasant symptoms after an accident or sports injury and war/disaster? Emotional, verbal, sexual, financial, religious, political, physical, resource and other controls, and manipulation abuses?"
    },
    {
      title: "Low Self-Esteem And Self-Worth",
      content: "Insecurity, less self-appreciation, putting self-down, low or no resilience. Lack of confidence."
    },
    {
      title: "Parents And Children In Distress",
      content: "Family reunification after children returned from foster care. A child at risk of aggressive or behavioral disorders. Abusive or neglectful parenting, child abuse or neglect. Difficulties in parenting."
    },
    {
      title: "Physical Pain Or Conditions",
      content: "Tension headaches, intensely fatigued and exhausted (Chronic Fatigue Syndrome/Medically Unexplained Symptoms (MUS), fibromyalgia, diabetes, arthritis, backache, physical disability, auditory processing disorder, cognitive/memory problems, tinnitus, hyperacusis, cardiac conditions, eating disorders, gastrointestinal disorders/ IBS, HIV, musculoskeletal conditions, neurological disorders, oncology/cancer, etc."
    },
    {
      title: "Stress From Exposure",
      content: "Stress or distress with coping with someone's health issue (carers, friends, and families with carers' stress) or for exposure to a crisis or a long-term activity/health condition"
    },
    {
      title: "Relationship Issues",
      content: "Not getting along with people? Relationship conflicts, crisis, betrayals, jealousy, envy, breakups or the joy/spark of your marriage or relationship declining or gone."
    },
    {
      title: "Workplace And Business Wellbeing",
      content: "Organisational, workplace and staff wellbeing. Resilience and work-related stress/burnout or trauma (bullying, harassment, discrimination, neglect, safeguarding, and grievance/formal proceedings, job satisfaction, work/life balance. Over or under working, work or academic performance anxiety, undervalued, being put/shut down."
    },
    {
      title: "Self-Harming",
      content: "Are you in distress and struggling with a desire to hurt yourself?"
    },
    {
      title: "Stress And Anxiety",
      content: "Worrying about things too much? Feels overwhelmed? Panic, on edgy. Stressed out due to life's multiple demands on you?"
    },
    {
      title: "Sex Or Gender Issues",
      content: "Sexual difficulties causing distress and Lesbian Gay, Bisexual and Transgender (LGBT) identity issues."
    }
  ];
  
  const additionalIssues = [
    "Social Or Public Speaking Anxiety",
    "Pain Management",
    "Sport Performance",
    "Driving Anxiety",
    "Female Genital Mutilation (FGM)",
    "Fatigue",
    "Child Abuse/Neglect",
    "Autistic Spectrum Disorder",
    "Anger, Irritable Mood, And Challenging Behaviours",
    "Cross-Cultural Or Faith Related Wellbeing Issues",
    "Eating, Weight, Or Body Image Issues",
    "Family Issues",
    "Grief/Bereavement And Loss",
    "Emotional Problems Induced By Financial Issues",
    "Indecision Or Procrastination",
    "Low/No Life Meaning And Purpose",
    "Mild Schizophrenia",
    "Personality Issues",
    "Pregnancy Related Issues",
    "Migrants And Refugees Adjustment Issues",
    "Re-Offending Issues",
    "Sleeping Problems",
    "Domestic Violence"
  ];

  const familyIssues = [
    "Conflicts, domestic violence, and Female Genital Mutilation (FGM)",
    "Emotional blocks for infertility",
    "Parents and children in distress",
    "Family reunification after children returned from foster care",
    "Child at risk of aggressive or behavioural disorders",
    "Abusive or neglectful parenting, child abuse or neglect",
    "Difficulties in parenting",
    "Migrants, refugees and business men and women adjustment and transitional issues",
    "Trauma, abuse and PTSD",
    "Emotional problems induced by financial (e.g. debt), housing or legal situations/needs",
    "Pregnancy related-occupational and psychological issues",
    "My problem not on this list, so I shall provide you the details myself"
  ];
  
  return (
    <div className="min-h-screen bg-white w-full">
      {/* Header section */}
      <header 
        className="w-full py-8 px-4 md:px-8 lg:px-16 bg-gradient-to-r from-gray-100 to-gray-200"
        style={{ backgroundColor: secondaryColor }}
      >
        <div className="max-w-6xl mx-auto">
          <h1 
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
            style={{ color: primaryColor }}
          >
            Issues We Solve
          </h1>
          <p className="text-gray-700 text-base md:text-lg lg:text-xl max-w-4xl">
            At Health City, we provide both in-person and online appointments. We specialize in
            treating a wide range of mental health conditions, including those mentioned below. Our
            evidence-based therapy is designed to help individuals solve and/or manage practical
            problems, thoughts, emotions, bodily reactions, behaviors, decision making or relationships.
            We also offer personalized care for many other mental health issues that may not be
            mentioned in this page.
          </p>
        </div>
      </header>

      {/* Main content section */}
      <main className="py-8 px-4 md:px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          {/* Main issues section with cards */}
          <section className="mb-16">
            <h2 
              className="text-2xl md:text-3xl font-bold mb-8 border-b-2 pb-2"
              style={{ borderColor: primaryColor, color: primaryColor }}
            >
              Common Mental Health Concerns
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {issues.map((issue, index) => (
                <div 
                  key={index}
                  className="border rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                  style={{ borderColor: expandedCard === index ? accentColor : 'rgba(0,0,0,0.1)' }}
                >
                  <button 
                    className="w-full text-left p-4 flex justify-between items-center"
                    onClick={() => toggleCard(index)}
                    style={{ 
                      backgroundColor: expandedCard === index ? primaryColor : 'white',
                      color: expandedCard === index ? 'white' : 'black'
                    }}
                  >
                    <h3 className="text-lg font-semibold">{issue.title}</h3>
                    <span className="text-xl">
                      {expandedCard === index ? '−' : '+'}
                    </span>
                  </button>
                  
                  <div 
                    className="overflow-hidden transition-all duration-300"
                    style={{
                      maxHeight: expandedCard === index ? '500px' : '0',
                      opacity: expandedCard === index ? 1 : 0
                    }}
                  >
                    <div className="p-4 bg-gray-50">
                      <p className="text-gray-700">{issue.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Additional issues section */}
          <section className="mb-16">
            <h2 
              className="text-2xl md:text-3xl font-bold mb-8 border-b-2 pb-2"
              style={{ borderColor: primaryColor, color: primaryColor }}
            >
              Additional Areas of Support
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {additionalIssues.map((issue, index) => (
                <div 
                  key={index} 
                  className="bg-gray-50 rounded-md p-3 border-l-4"
                  style={{ borderLeftColor: primaryColor }}
                >
                  <p className="font-medium">{issue}</p>
                </div>
              ))}
            </div>
          </section>
          
          {/* Family issues section */}
          <section>
            <h2 
              className="text-2xl md:text-3xl font-bold mb-8 border-b-2 pb-2"
              style={{ borderColor: primaryColor, color: primaryColor }}
            >
              Family-Related Issues
            </h2>
            
            <div className="bg-gray-50 rounded-lg p-6 border">
              <h3 
                className="text-xl mb-4 font-semibold"
                style={{ color: primaryColor }}
              >
                Family Issues
              </h3>
              
              <ul className="list-disc pl-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                {familyIssues.map((issue, index) => (
                  <li key={index} className="text-gray-700">{issue}</li>
                ))}
              </ul>
            </div>
          </section>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-gray-100 py-8 px-4 md:px-8 mt-12">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-600">
            If you're experiencing an issue that's not listed here, please contact us for personalized support.
          </p>
          <div className="mt-6">
            <button 
              onClick={() => navigate('/contact')}
              className="px-6 py-2 rounded-md font-medium text-white hover:bg-opacity-90 transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}